/**
 * Generic component for questions with custom labels
 */

import { Box } from '@mui/material';
import { useState } from 'react';
import { CustomScreenerTypes } from '../../../../../../../../modules/intake/constants';
import { ValidationErrorNotice } from '../../../../../../components/Notice';
import { AddListItemButton } from '../../../../../../components/SelectableTile';
import Subheader from '../../../../../../components/Subheader';
import { CustomScreenerTypeOptions } from '../../../../constants';
import { serializeErrors } from '../../../../helpers/validation';
import CustomChoiceAndOutcome from '../CustomChoiceAndOutcome';

function MultipleChoiceConfiguration({
  customScreener,
  showHeading = true,
  onCustomScreenerChanged,
  onOptionAdded,
  onOptionDeleted,
  isAddingOption,

  errors,
}) {
  const [deleteCounter, setDeleteCounter] = useState(0);

  const type = customScreener?.questionType;
  const questionTypeLimits = CustomScreenerTypeOptions.find((option) => option.value === type)?.choiceLimits;

  const minChoices = questionTypeLimits?.min || 0;
  const maxChoices = questionTypeLimits?.max || 0;

  const options = customScreener?.options;

  const canAddOptions = options.length < maxChoices;
  const isDeleteable = [CustomScreenerTypes.MultipleChoice, CustomScreenerTypes.MultiSelect].includes(type);
  // this condition should only be true for actual multiple choice questions, not custom scale labels
  const canDeleteChoices = minChoices < maxChoices && options.length > minChoices;

  const handleChoiceAdded = () => {
    onOptionAdded(customScreener.id);
  };

  const handleChoiceChanged = (optionId, newValue, oldValue) => {
    if (newValue === oldValue) {
      return;
    }
    const updateFields = { optionText: newValue };
    onCustomScreenerChanged({ customScreener, optionId, updateFields });
  };

  const handleOutcomeChanged = (optionId, newOutcome, oldOutcome) => {
    if (newOutcome === oldOutcome) {
      return;
    }
    const updateFields = { outcome: newOutcome };
    onCustomScreenerChanged({ customScreener, optionId, updateFields });
  };

  const handleChoiceRemoved = (optionId) => {
    onOptionDeleted({ customScreenerId: customScreener?.id, optionId });
    setDeleteCounter(deleteCounter + 1);
  };

  return (
    <Box>
      {showHeading && (
        <Subheader
          name={'Enter your custom choices'}
          description={'Choices will be randomized to minimize survey bias.'}
        />
      )}
      <Box my={2}>
        {options.map((option, index) => (
          /**
            It's VERY IMPORTANT that the key changes when the label changes and that we include the
            index so that there are no key collisions. This is because the underlying choice text field
            uses internal state and debounces updates. A consequence of this is that if not remounted, the
            internal state will not match the new label state. Unfortunately, this trades off performance
            on deletes, since we're remounting many times when a choice gets deleted
          */
          <Box key={`${index}_${deleteCounter}`} mb={1}>
            <CustomChoiceAndOutcome
              optionAndOutcome={option}
              questionType={type}
              isDeletable={isDeleteable}
              canDeleteChoice={canDeleteChoices}
              onCustomChoiceChange={(newValue) =>
                handleChoiceChanged(option?.id, newValue, option?.optionText)
              }
              onOutcomeChange={(newOutcome) => handleOutcomeChanged(option?.id, newOutcome, option?.outcome)}
              onCustomChoiceDelete={() => handleChoiceRemoved(option?.id)}
            />
          </Box>
        ))}
        {errors?.labels?.length > 0 && (
          <Box my={1}>
            <ValidationErrorNotice message={serializeErrors(errors.labels)} />
          </Box>
        )}
      </Box>
      {canAddOptions && (
        <Box mt={4}>
          <AddListItemButton
            width="100%"
            size="small"
            isLoading={isAddingOption}
            onClick={isAddingOption ? () => {} : handleChoiceAdded}
          />
        </Box>
      )}
    </Box>
  );
}

export default MultipleChoiceConfiguration;
